import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext, useNavigate, useBlocker } from 'react-router-dom';
//import AnimateOnChange from 'react-animate-on-change';
import { motion, AnimatePresence } from "framer-motion";

import { createDeck, updateDeck, fetchDeck } from '../features/decks/decksSlice';

import { useSearchParams } from 'react-router-dom';

import CardSearch from './card_search';
import CardViewer from './card_view';

import { getFormats } from '../constants/formats';
import { getCardKey } from '../helpers/getCardKey';

import axios from 'axios';

export default function NewDeck() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');

  const [showSort, setShowSort] = useState(false);

  const [sortDraft, setSortDraft] = useState({
    orderby: '',
    direction: 'asc'
  });

  const [sort, setSort] = useState({
    orderby: '',
    direction: 'asc'
  });
  
  const [deckName, setDeckName] = useState('');
  const [deckCreator, setDeckCreator] = useState('');
  const [deckFormat, setDeckFormat] = useState('');
  const [deckDescription, setDeckDescription] = useState('');

  const [mainDeckArray, setMainDeckArray] = useState([]);
  const [maybeboardArray, setMaybeboardArray] = useState([]);

  const [view, setView] = useState('search'); 
  // 'search' | 'main' | 'maybe' | 'props'
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  //const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [animDir, setAnimDir] = useState('right');
  const prevView = React.useRef(view);
  
  const selectedDeck = useSelector(state => state.decks.selectedDeck);

  const [layout, setLayout] = useState('list'); // 'list' | 'grid'

  const [page, setPage] = useState(1);
  
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');

  useEffect(() => {
    if (editId) {
      dispatch(fetchDeck(editId));
    }
  }, [editId, dispatch]);

  //useEffect(() => {
  //  const handler = () => setIsMobile(window.innerWidth < 768);
  //  window.addEventListener('resize', handler);
  //  return () => window.removeEventListener('resize', handler);
  //}, []);

  useEffect(() => {
    if (!selectedDeck || !editId) return;

    setDeckName(selectedDeck.name || '');
    setDeckCreator(selectedDeck.creator || '');
    setDeckFormat(selectedDeck.format || '');
    setDeckDescription(selectedDeck.description || '');
    setMainDeckArray(selectedDeck.cards || []);
    setMaybeboardArray(selectedDeck.maybeboard || []);
    setIsDirty(false);
  }, [selectedDeck, editId]);

  useEffect(() => {
    if (!editId) {
      setDeckName('');
      setDeckCreator('');
      setDeckFormat('');
      setDeckDescription('');
      setMainDeckArray([]);
      setMaybeboardArray([]);
      setIsDirty(false);
    }
  }, [editId]);

  // unsaved changes protection
  const blocker = useBlocker(isDirty && !isSaving);

  useEffect(() => {
    if (blocker.state === "blocked") {
      const ok = window.confirm(
        "You have unsaved changes. Leave anyway?"
      );

      if (ok) blocker.proceed();
      else blocker.reset();
    }
  }, [blocker]);

  useEffect(() => {
  const handleBeforeUnload = (e) => {
    if (!isDirty) return;

    // Required for Chrome/Edge/Firefox
    e.preventDefault();
    e.returnValue = "";
  };

  window.addEventListener("beforeunload", handleBeforeUnload);

  return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  // sort cards
  const sortCards = useCallback((cards) => {
    if (!sort.orderby) return cards;
    
    const sorted = [...cards];
    
    sorted.sort((a, b) => {
      let aVal;
      let bVal;
    
      switch (sort.orderby) {
        case 'cost':
          aVal = a.cost ?? a.Cost ?? 0;
          bVal = b.cost ?? b.Cost ?? 0;
          break;
      
        case 'strength':
          aVal = a.Strength || 0;
          bVal = b.Strength || 0;
          break;
      
        case 'lore':
          aVal = a.Lore || 0;
          bVal = b.Lore || 0;
          break;
      
        case 'name':
          aVal = a.name || a.Name || '';
          bVal = b.name || b.Name || '';
      
          return sort.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
      
        case 'color':
          aVal = a.Color || '';
          bVal = b.Color || '';
      
          return sort.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
      
        case 'rarity':
          aVal = a.Rarity || '';
          bVal = b.Rarity || '';
      
          return sort.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
      
        default:
          return 0;
      }
    
      return sort.direction === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    });
  
    return sorted;
  }, [sort]);

  const groupCards = (list) => {
    return Object.values(
      list.reduce((acc, card) => {
        const key = getCardKey(card);
      
        if (!acc[key]) {
          acc[key] = { card, count: 0 };
        }
      
        acc[key].count += 1;
        return acc;
      }, {})
    );
  };

  const markDirty = (setter) => (value) => {
    setter(value);
    setIsDirty(true);
  };

  const changeView = (next) => {
    if (next === view) return;

    setAnimDir(
      next === 'search' ? 'left' :
      next === 'main' ? 'left' :
      next === 'maybe' ? 'right' :
      'right'
    );

    prevView.current = view;
    setView(next);
  };

  const addMaybeboard = (card) => {
    setMaybeboardArray(prev => [...prev, card]);
    setIsDirty(true);
  };

  const removeCard = (card) => {
    const key = getCardKey(card);

    setMainDeckArray(prev => {
      const index = prev.findIndex(c => getCardKey(c) === key);
      if (index === -1) return prev;

      return [
        ...prev.slice(0, index),
        ...prev.slice(index + 1)
      ];
    });
    setIsDirty(true);
  };

  const removeMaybeboard = (card) => {
    const key = getCardKey(card);

    setMaybeboardArray(prev => {
      const index = prev.findIndex(c => getCardKey(c) === key);
      if (index === -1) return prev;

      return [
        ...prev.slice(0, index),
        ...prev.slice(index + 1)
      ];
    });
    setIsDirty(true);
  };

  // -------------------------
  // SAVE
  // -------------------------

  const saveDeck = useCallback(async () => {
    //if (!deckName) return setError('name');
    //if (!deckFormat) return setError('format');
    //if (mainDeckArray.length === 0) return setError('cards');
    setIsSaving(true);


    const deck = {
      id: editId ? Number(editId) : undefined,
      name: deckName ? deckName : "Untitled Deck",
      creator: deckCreator ? deckCreator : "Anonymous",
      format: deckFormat ? deckFormat : "No Limit",
      description: deckDescription,
      cards: mainDeckArray,
      maybeboard: maybeboardArray,
      colors: [...new Set(mainDeckArray.map(c => c.color))]
    };

    if (editId) {
      await dispatch(updateDeck(deck));
    } else {
      const result = await dispatch(createDeck(deck));
      console.log("createresult", result);  
      navigate(`/decks/new?edit=${result.payload.id}`);
    }

    setIsDirty(false);
    setIsSaving(false);


  }, [deckName, deckCreator, deckFormat, deckDescription, mainDeckArray, maybeboardArray, editId, dispatch, navigate]);

  const handleImport = async () => {
    if (!importText.trim()) return;

    const lines = importText.split('\n');

    const nameCounts = new Map();

    // Parse input
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      const match = trimmed.match(/^(\d+)\s+(.+)$/);
      if (!match) continue;

      const amount = parseInt(match[1], 10);
      const name = match[2].trim();

      nameCounts.set(name, (nameCounts.get(name) || 0) + amount);
    }

    if (nameCounts.size === 0) return;

    // Build strict query
    const names = Array.from(nameCounts.keys());
    const strictParam = names.join(';');

    try {
      const res = await axios.get(
        `https://api.lorcana-api.com/cards/fetch?strict=${encodeURIComponent(strictParam)}`
      );

      const cards = Array.isArray(res.data)
        ? res.data
        : res.data?.data || [];

      // Index results
      const cardMap = new Map();

      cards.forEach(card => {
        cardMap.set(card.Name.toLowerCase(), card);
      });

      const newCards = [];
      const notFound = [];

      // Build deck
      for (const [name, count] of nameCounts.entries()) {
        const found = cardMap.get(name.toLowerCase());

        if (!found) {
          notFound.push(name);
          continue;
        }

        for (let i = 0; i < count; i++) {
          newCards.push(found);
        }
      }

      // Apply
      if (newCards.length > 0) {
        setMainDeckArray(prev => [...prev, ...newCards]);
        setIsDirty(true);
      }

      if (notFound.length > 0) {
        alert(`These cards were not found:\n${notFound.join('\n')}`);
      }

    } catch (err) {
      console.error('Import failed:', err);
      alert('Import failed. Please try again.');
    }

    setImportOpen(false);
    setImportText('');
    changeView('main');
  };

  const { setCanSave, setOnSave } = useOutletContext();
  useEffect(() => {
    setCanSave(isDirty);
    setOnSave(saveDeck);
  }, [isDirty, saveDeck, setCanSave, setOnSave]);

  //mobile card view

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerCards, setViewerCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openViewer = (cardsArray, clickedCard) => {
    const seen = new Map();

    const uniqueCards = cardsArray.filter((c) => {
      const key = getCardKey(c);
      if (seen.has(key)) return false;
      seen.set(key, true);
      return true;
    });

    const clickedKey = getCardKey(clickedCard);

    const index = uniqueCards.findIndex(
      c => getCardKey(c) === clickedKey
    );

    setViewerCards(uniqueCards);
    setCurrentIndex(index >= 0 ? index : 0);

    //if (window.innerWidth < 768) {
    //  setViewerOpen(true);
    //}
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setCurrentIndex(0);
  };

  const getCount = (card) => {
    const key = getCardKey(card);
    return mainDeckArray.filter(c => getCardKey(c) === key).length;
  };

  const increaseCard = (card) => {
    setMainDeckArray(prev => [...prev, card]);
    setIsDirty(true);
  };

  const decreaseCard = (card) => {
    if (getCount(card) === 0) return;
    const key = getCardKey(card);
    
    setMainDeckArray(prev => {
      const index = prev.findIndex(c => getCardKey(c) === key);
      if (index === -1) return prev;
    
      return [
        ...prev.slice(0, index),
        ...prev.slice(index + 1)
      ];
    });
    setIsDirty(true);
  };

  const CardImage = ({ src, alt, extraKey }) => {
    return (
      <img
        key={extraKey ? `${src}-${extraKey}` : src}
        src={src}
        alt={alt}
        style={{ opacity: 1 }}
      />
    );
  };

  // Rendering
  const renderGrid = (list) => {
    const grouped = groupCards(list);

    return (
      <div className="card_grid">
        {grouped.map(({ card, count }) => (
          <div
            key={getCardKey(card)}
            className="card_grid_item"
            onClick={() => openViewer(list, card)}
          >
            <CardImage
              src={card.Image}
              alt={card.Name}
              extraKey={view}
            />

            <div
              className="card_grid_controls"
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => decreaseCard(card)}>−</button>
                <div className="card_count_badge_grid">
                  {count}
                </div>
              <button onClick={() => increaseCard(card)}>+</button>
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  const renderList = (list, removeFn) => {
    const grouped = list.reduce((acc, card) => {
      const key = getCardKey(card);
    
      if (!acc[key]) {
        acc[key] = { card, count: 0 };
      }
    
      acc[key].count += 1;
      return acc;
    }, {});
  
    return Object.values(grouped)
      //.sort((a, b) => (a.card.Name || '').localeCompare(b.card.Name || ''))
      .map(({ card, count }) => (
        <div key={getCardKey(card)} className="deck_row"
            onClick={() => openViewer(list, card)}>
        
          <div
            className="card_name"
          >
            <strong className="card_title">
      
              <span
                className={`card_count ${card.Inkable ? 'inkable' : 'not_inkable'}`}
              >
                {card.Cost}
              </span>
      
              {card.Name}
      
            </strong>
          </div>
      
          <div className="card_controls"
            onClick={(e) => e.stopPropagation()}>
            <button
              className="ctrl_btn minus"
              onClick={() => decreaseCard(card)}
            >
              −
            </button>
      
            <span className="count_display">{count}</span>
      
            <button
              className="ctrl_btn plus"
              onClick={() => increaseCard(card)}
            >
              +
            </button>
          </div>
      
        </div>
      ));
  };

  return (
    <div className="container deck">
        <div className={`mobile_view_container ${animDir}`}>

          <AnimatePresence mode="wait">
            <motion.div
              key={view}
              initial={{ x: 300}}
              animate={{ x: 0}}
              exit={{ x: -300}}
              transition={{ duration: 0.3 }}
            >
        
          {view === 'search' && (
            <div>
              <div className="layout_switch">
                <div
                  className={`switch_track ${layout === 'grid' ? 'grid' : 'list'}`}
                  onClick={() => setLayout(prev => prev === 'list' ? 'grid' : 'list')}
                >
                  <div className="switch_thumb" />
                        
                  <i className="fa fa-list switch_icon left" />
                  <i className="fa fa-th switch_icon right" />
                </div>
              </div>
              <CardSearch
                layout={layout}
                setLayout={setLayout}
                openViewer={openViewer}
                getCount={getCount}
                onIncrease={increaseCard}
                onDecrease={decreaseCard}
                page={page}
                setPage={setPage}
                setImportOpen={setImportOpen}
                deckFormat={deckFormat}
              />
            </div>
          )}

          {view === 'main' && (
            <div className="deck_output">
              <div className="layout_switch">
                <div
                  className={`switch_track ${layout === 'grid' ? 'grid' : 'list'}`}
                  onClick={() => setLayout(prev => prev === 'list' ? 'grid' : 'list')}
                >
                  <div className="switch_thumb" />
                        
                  <i className="fa fa-list switch_icon left" />
                  <i className="fa fa-th switch_icon right" />
                </div>
              </div>
              <div className="deck_view_header">
                <h5>Main Deck</h5>
                <button
                  className="advanced_btn"
                  onClick={() => setShowSort(true)}
                >
                  <i className="fa fa-sort" />
                </button>
              </div>
              {layout === 'grid'
                ? renderGrid(sortCards(mainDeckArray))
                : renderList(sortCards(mainDeckArray), removeCard)}
            </div>
          )}

          {view === 'maybe' && (
            <div className="deck_output">
              <div className="layout_switch">
                <div
                  className={`switch_track ${layout === 'grid' ? 'grid' : 'list'}`}
                  onClick={() => setLayout(prev => prev === 'list' ? 'grid' : 'list')}
                >
                  <div className="switch_thumb" />
                        
                  <i className="fa fa-list switch_icon left" />
                  <i className="fa fa-th switch_icon right" />
                </div>
              </div>
              <div className="deck_view_header">
                <h5>Maybe</h5>
                <button
                  className="advanced_btn"
                  onClick={() => setShowSort(true)}
                >
                  <i className="fa fa-sort" />
                </button>
              </div>
              {layout === 'grid'
                ? renderGrid(sortCards(maybeboardArray))
                : renderList(sortCards(maybeboardArray), removeMaybeboard)}
            </div>
          )}

          {view === 'props' && (
            <div className="deck_inputs">
              <div className="deck_inputs_group">
                <label>Deck Name</label>
                <input
                  value={deckName}
                  onChange={(e) => markDirty(setDeckName)(e.target.value)}
                />
              </div>

              <div className="deck_inputs_group">
                <label>Created by</label>
                <input
                  value={deckCreator}
                  onChange={(e) => markDirty(setDeckCreator)(e.target.value)}
                />
              </div>

              <div className="deck_inputs_group">
                <label>Deck Format</label>
                <select
                  value={deckFormat}
                  onChange={(e) => markDirty(setDeckFormat)(e.target.value)}
                >
                  <option value="">Select Format</option>
                  {getFormats().map(format => (
                    <option key={format} value={format}>
                      {format}
                    </option>
                  ))}
                </select>
              </div>

              

        {/* DESCRIPTION */}
        <div className="deck_bottom">
          <label>Description</label>
          <textarea
            value={deckDescription}
            onChange={(e) => markDirty(setDeckDescription)(e.target.value)}
          />
        </div>

            </div>
          )}

          </motion.div>
        </AnimatePresence>
        </div>

      {/* ========================= */}
      {/* MOBILE BOTTOM NAV */}
      {/* ========================= */}
      <div className="mobile_bottombar">
        <button onClick={() => changeView('props')} className="nav_btn">
          <i className="fa fa-cog" />
        </button>

        <button onClick={() => changeView('search')} className="nav_btn">
          <i className="fa fa-search" />
        </button>

        <button onClick={() => changeView('main')} className="nav_btn">
          <i className="fa fa-list" />
        </button>

        <button onClick={() => changeView('maybe')} className="nav_btn">
          <i className="fa fa-star-o" />
        </button>
      </div>

      {importOpen && (
        <div className="importmodal_overlay" onClick={() => setImportOpen(false)}>
          <div className="importmodal_content" onClick={(e) => e.stopPropagation()}>

            <h3>Import Deck</h3>

            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder={`4 Mother Gothel - Selfish Manipulator
4 Do It Again!
4 Alice - Growing Girl`}
              rows={10}
            />

            <div className="importmodal_actions">
              <button className="advanced_cancel_btn" onClick={() => setImportOpen(false)}>
                Cancel
              </button>

              <button onClick={handleImport}>
                Import
              </button>
            </div>
              
          </div>
        </div>
      )}

      {showSort && (
        <div
          className="advanced_overlay"
          onClick={() => setShowSort(false)}
        >
          <div
            className="advanced_modal"
            onClick={(e) => e.stopPropagation()}
          >
          
            <div className="advanced_grid">

              <div className="filter_row">

                <div className="filter_field">
                  <label>Sort By</label>

                  <select
                    value={sortDraft.orderby}
                    onChange={e =>
                      setSortDraft(s => ({
                        ...s,
                        orderby: e.target.value
                      }))
                    }
                  >
                    <option value="">None</option>
                    <option value="cost">Cost</option>
                    <option value="strength">Strength</option>
                    <option value="lore">Lore</option>
                    <option value="name">Name</option>
                    <option value="rarity">Rarity</option>
                    <option value="color">Color</option>
                  </select>
                </div>
                  
                <div className="filter_field">
                  <label>Direction</label>
                  
                  <select
                    value={sortDraft.direction}
                    disabled={!sortDraft.orderby}
                    onChange={e =>
                      setSortDraft(s => ({
                        ...s,
                        direction: e.target.value
                      }))
                    }
                  >
                    <option value="asc">Ascending</option>
                    <option value="desc">Descending</option>
                  </select>
                </div>
                  
              </div>
                  
            </div>
                  
            <div className="advanced_actions">
                  
              <button
                onClick={() => {
                  setSortDraft({
                    orderby: '',
                    direction: 'asc'
                  });
                }}
              >
                Reset
              </button>

              <div>
                <button className="advanced_cancel_btn"
                  onClick={() => {
                    setShowSort(false);
                  }}
                >
                  Cancel
                </button>
                
                <button
                  onClick={() => {
                    setSort(sortDraft);
                    setShowSort(false);
                  }}
                >
                  Apply
                </button>
              </div>
              
            </div>
              
          </div>
        </div>
      )}

      {/* CARD VIEWER */}
      <CardViewer
        open={viewerOpen}
        cards={viewerCards}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        onClose={handleCloseViewer}
        onAddMaybeboard={addMaybeboard}
        getCount={getCount}
        onIncrease={increaseCard}
        onDecrease={decreaseCard}
        showEditControls={true}
      />

    </div>
  );
}